import type { LibraryContext } from "@/native-shell/library";
import { assertOfflineContext } from "@/lib/offline/context";
import { readCompanionResponse } from "../http";
import { freestyleObject } from "./clips";
import { UUID } from "../outings";
import { COMPANION_SHARED_PLANS_CHANGED } from "../events";
import { validCommunityCommand, type CommunityCommand } from "./community";

/** No media or community response is stored in an offline cache. */
export async function communityRequest(
  context: LibraryContext,
  params: Record<string, string>,
  command?: CommunityCommand,
  signal?: AbortSignal,
): Promise<Record<string, unknown>> {
  await assertOfflineContext(context);
  if (context.owner === "device")
    throw new Error("Sign in to open your Freestyle communities.");
  if (!navigator.onLine)
    throw new Error(
      "Reconnect to check current access to shared clips. Your drafts stay on this device.",
    );
  if (command && !validCommunityCommand(command))
    throw new Error("Review this change before sending it.");
  const value = await fetch(
    `/api/companion/freestyle/community?${new URLSearchParams(params)}`,
    {
      cache: "no-store",
      signal: AbortSignal.any([
        ...(signal ? [signal] : []),
        AbortSignal.timeout(12_000),
      ]),
      ...(command
        ? {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ owner: context.owner, command }),
          }
        : {}),
    },
  ).then((response) => readCompanionResponse(response, 524288));
  await assertOfflineContext(context);
  if (!freestyleObject(value) || value.owner !== context.owner)
    throw new Error("Your account changed. Reopen Freestyle.");
  return value;
}

export async function blockFreestylePerson(
  context: LibraryContext,
  userId: string,
): Promise<void> {
  await assertOfflineContext(context);
  if (
    context.owner === "device" ||
    !UUID.test(userId) ||
    userId === context.owner
  )
    throw new Error("Choose another person to block.");
  await fetch("/api/companion/people", {
    method: "POST",
    cache: "no-store",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      owner: context.owner,
      command: { action: "block", userId },
    }),
    signal: AbortSignal.timeout(12_000),
  }).then((response) => readCompanionResponse(response, 524288));
  await assertOfflineContext(context);
  window.dispatchEvent(new Event(COMPANION_SHARED_PLANS_CHANGED));
}
